import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Loader2, Settings, HelpCircle, PlayCircle, Menu, X, Tag, FileText, Mail, BookOpen, CreditCard } from 'lucide-react';

const MobileHeader = ({
  user,
  profile,
  displayName,
  avatarUrl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  adminCheckLoading,
  userIsAdmin,
  handleStartOnboarding
}) => {
  const navigate = useNavigate();
  const menuButtonRef = useRef(null);
  const [menuTop, setMenuTop] = React.useState('60px');

  useEffect(() => {
    if (isMobileMenuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const headerHeight = rect.bottom;
      setMenuTop(`${headerHeight}px`);
    }
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* 모바일 메뉴 토글 버튼 */}
      <button
        ref={menuButtonRef}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="block lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="메뉴 열기"
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* 모바일 드롭다운 메뉴 */}
      {isMobileMenuOpen && (
        <>
          {/* 오버레이 */}
          <div 
            className="fixed inset-0" 
            style={{ zIndex: 998 }}
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* 메뉴 */}
          <div className="lg:hidden border-t border-gray-200 bg-white fixed left-0 right-0 shadow-lg" style={{ zIndex: 999, top: menuTop }}>
          <div className="px-4 py-3 space-y-2">
            {user ? (
              <>
                {/* 사용자 정보 */}
                <button
                  onClick={() => {
                    navigate('/profile');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 py-2 border-b border-gray-100 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt={displayName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-700">{displayName}</p>
                    <p className="text-xs text-gray-500">내 프로필</p>
                  </div>
                </button>
                
                {/* 메뉴 아이템들 */}
                <button
                  onClick={() => {
                    navigate('/documents');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  이력서 목록
                </button>
                
                <button
                  onClick={() => {
                    window.open('/guide', '_blank', 'noopener,noreferrer');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                  사용자 가이드
                </button>
                
                <button
                  onClick={() => {
                    window.open('https://latteis.com/blog', '_blank', 'noopener,noreferrer');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  블로그
                </button>
                
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  플랜 및 가격
                </button>
                
                <button
                  onClick={() => {
                    handleStartOnboarding();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <PlayCircle className="w-4 h-4" />
                  온보딩 투어
                </button>
                
                {/* 관리자 메뉴 */}
                {!adminCheckLoading && userIsAdmin && (
                  <>
                    <div className="border-t border-gray-100 my-2"></div>
                    <button
                      onClick={() => {
                        navigate('/admin-prompts');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      프롬프트 관리
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin-users');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      사용자 관리
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin-promo-codes');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Tag className="w-4 h-4" />
                      프로모션 코드
                    </button>
                    <button
                      onClick={() => {
                        navigate('/admin-promo-requests');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      프로모션 요청
                    </button>
                  </>
                )}
              </>
            ) : (
              /* 로그인하지 않은 사용자를 위한 모바일 메뉴 */
              <>
                <button
                  onClick={() => {
                    window.open('/guide', '_blank', 'noopener,noreferrer');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                  사용자 가이드
                </button>
                
                <button
                  onClick={() => {
                    window.open('https://latteis.com/blog', '_blank', 'noopener,noreferrer');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  블로그
                </button>
                
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  플랜 및 가격
                </button>
                
                <div className="border-t border-gray-100 my-2"></div>
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-primary-600 font-medium hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                  로그인
                </button>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </>
  );
};

export default MobileHeader;
